/**
 * PlanningKanban — board with one column per task-state (+ "Sin estado").
 * Cards are scheduled items; drag a card to another column to change its
 * state (planner.task-states). Click a card to edit.
 */
import { useState } from 'react';
import {
  type PlannerResource,
  type PlannerService,
  type PlannerScheduledItem,
  type PlannerTaxonomies,
} from '@/services/api';
import { hm, treeById, STATE_COLORS } from './plannerShared';

export default function PlanningKanban({ items, resources, services, tax, canManage, uncoveredItemIds, onEdit, onChangeState }: {
  items: PlannerScheduledItem[];
  resources: PlannerResource[];
  services: PlannerService[];
  tax: PlannerTaxonomies | null;
  canManage: boolean;
  uncoveredItemIds?: Set<string>;
  onEdit: (item: PlannerScheduledItem) => void;
  onChangeState: (id: string, stateNodeId: string | null) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<string>('');

  const resName = (id: string) => resources.find((r) => r._id === id)?.name || '—';
  const svcName = (id?: string | null) => services.find((s) => s._id === id)?.name || 'Servicio';

  const stateNodes = treeById(tax, 'task-states')?.nodes || [];
  // Columns: each state + a trailing "Sin estado" (null).
  const columns: { key: string; nodeId: string | null; name: string; slug?: string }[] = [
    ...stateNodes.map((n) => ({ key: n._id, nodeId: n._id, name: n.name, slug: n.id })),
    { key: '__none__', nodeId: null, name: 'Sin estado' },
  ];

  const colItems = (nodeId: string | null) =>
    items
      .filter((it) => (it.stateNodeId || null) === nodeId)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const drop = (nodeId: string | null) => {
    if (dragId) {
      const it = items.find((x) => x._id === dragId);
      if (it && (it.stateNodeId || null) !== nodeId) onChangeState(dragId, nodeId);
    }
    setDragId(null);
    setOverCol('');
  };

  if (stateNodes.length === 0) {
    return <p className="text-sm text-gray-500">No hay estados definidos. Revisa el árbol "Estados de Actuación" en Metadatos.</p>;
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {columns.map((col) => {
        const list = colItems(col.nodeId);
        const dot = (col.slug && STATE_COLORS[col.slug]) || 'bg-gray-400';
        return (
          <div
            key={col.key}
            onDragOver={(e) => { if (canManage) { e.preventDefault(); setOverCol(col.key); } }}
            onDragLeave={() => setOverCol((c) => (c === col.key ? '' : c))}
            onDrop={() => drop(col.nodeId)}
            className={`shrink-0 w-64 bg-gray-50 rounded-xl border ${overCol === col.key ? 'border-vida-400 bg-vida-50/40' : 'border-gray-200'}`}
          >
            <div className="px-3 py-2 flex items-center justify-between border-b border-gray-200">
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <span className={`w-2 h-2 rounded-full ${dot}`} /> {col.name}
              </span>
              <span className="text-xs text-gray-400">{list.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[80px]">
              {list.map((it) => {
                const uncovered = uncoveredItemIds?.has(it._id) || false;
                return (
                  <div
                    key={it._id}
                    draggable={canManage}
                    onDragStart={() => setDragId(it._id)}
                    onDragEnd={() => { setDragId(null); setOverCol(''); }}
                    onClick={() => onEdit(it)}
                    className={`rounded-lg border p-2 text-xs shadow-sm hover:shadow ${canManage ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${dragId === it._id ? 'opacity-50' : ''} ${uncovered ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}
                    title={uncovered ? 'El empleado asignado no tiene presencia planificada para este horario' : undefined}
                  >
                    <div className="font-medium text-gray-900 truncate">{svcName(it.serviceId)}</div>
                    <div className="text-gray-500 truncate">{resName(it.resourceId)}{it.clientLabel ? ` · ${it.clientLabel}` : ''}</div>
                    <div className="text-gray-400 mt-0.5">
                      {new Date(it.start).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })} · {hm(new Date(it.start))}
                      {it.alarmAt && ' ⏰'}
                    </div>
                    {uncovered && <div className="mt-1 text-[10px] text-amber-700 font-medium">⚠ Sin presencia asignada</div>}
                  </div>
                );
              })}
              {list.length === 0 && <p className="text-[11px] text-gray-300 text-center py-2">—</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
